import { ActionButton, WithActionBar } from "@/src/components/wrappers/WithActionBar";
import { EditYachtForm } from "./EditYacht/EditYachtForm";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { Content } from "@/src/components/wrappers/Content";
import { useStepsStore } from "./EditYacht/state";
import { Button } from "@/src/components/ui/button";
import { Steps } from "@/src/components/ui/steps";
import { emptyYachtMeta, serializeYachtForm, YachtForm, YachtLocationRef } from "./EditYacht/yacht.schema";
import { useFetch } from "@/src/lib/useFetch";
import { useToast } from "@/src/components/ui/use-toast";
import { useLoaderData, useNavigate, useSearchParams } from "react-router-dom";
import { fetch } from "@/src/lib/fetch";
import { useEffect, useMemo } from "react";
import { YachtDetails } from "./model";

export function EditYacht() {
  const yacht = useLoaderData() as YachtDetails & { generalRateLocationsById?: Record<string, YachtLocationRef> };
  const steps = useStepsStore();
  const { fetch, isFetching } = useFetch();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [params, _] = useSearchParams();

  console.log("yacht: ", yacht);

  const yachtForm = useMemo(() => yachtData(yacht), [yacht]);

  useEffect(() => {
    const step = params.get("step") as typeof steps.step;
    if (step) {
      steps.setStep(step);
    } else {
      steps.setStep("Summary");
    }
  }, []);

  const onSubmit = async (data: YachtForm) => {
    console.log("Submitting yacht data: ", data);
    if (!isFetching) {
      try {
        const payload = serializeYachtForm(data);
        await fetch(`/api/yachts/yacht/${yacht.id}`, "PUT", payload);
        toast({
          title: "Yacht change",
          description: "The yacht has been saved",
          variant: "default",
        });
        navigate(`/yachts`);
      } catch (e: any) {
        console.error(e);
        toast({
          title: "Failed to save yacht",
          description: e.message || "An error occurred while saving the yacht",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <WithActionBar actionBar={<ActionBar />}>
      <Content title="Add a non-API yacht">
        <Steps onStepChange={steps.setStep} step={steps.step} items={steps.allSteps} className="mb-6" />
        <EditYachtForm yacht={yachtForm} onSubmit={onSubmit} />
      </Content>
    </WithActionBar>
  );
}

function ActionBar() {
  const steps = useStepsStore();
  return (
    <div className="flex justify-between items-center">
      <Button onClick={steps.previousStep} disabled={steps.isFirstStep} variant="outline">
        <ChevronLeftIcon size={16} />
        Back
      </Button>
      <div className="flex items-center gap-4">
        <ActionButton variant="secondary" eventKey="yacht:save">
          Save
        </ActionButton>
        {!steps.isLastStep && (
          <Button onClick={steps.nextStep}>
            Next <ChevronRightIcon size={16} />
          </Button>
        )}
      </div>
    </div>
  );
}

function yachtData(yacht: YachtDetails & { generalRateLocationsById?: Record<string, YachtLocationRef> }): YachtForm {
  const helmPriceInfo = yacht.meta?.helmPriceInfo;
  const generalRates = yacht.meta?.generalRates ?? [];
  const toRateLocations = (locationIds: string[]): [YachtLocationRef, ...YachtLocationRef[]] => {
    const locations = locationIds
      .map((locationId) => yacht.generalRateLocationsById?.[locationId])
      .filter((location): location is YachtLocationRef => Boolean(location));
    return locations.length > 0 ? [locations[0], ...locations.slice(1)] : [yacht.location];
  };

  // Construct correct pricing shape based on type
  let pricing:
    | {
        type: "simple";
        location: {
          id: string;
          names: string;
        };
        price: number;
        currency: string;
        per: "day" | "week" | "charter" | "hour";
        vat: number;
        apa: number;
      }
    | {
        type: "seasonal";
        prices: {
          locations: [YachtLocationRef, ...YachtLocationRef[]];
          price: number;
          currency: string;
          per: "day" | "week" | "charter" | "hour";
          vat: number;
          apa: number;
          dateRange: { startDate: string; endDate: string };
        }[];
      };

  if (generalRates.length > 0) {
    pricing = {
      type: "seasonal",
      prices: generalRates.map((rate) => ({
        locations: toRateLocations(rate.locations),
        price: rate.priceInfo.price ?? 0,
        currency: rate.priceInfo.currency ?? "EUR",
        per: rate.priceInfo.per ?? "day",
        vat: rate.priceInfo.vat,
        apa: rate.priceInfo.apa,
        dateRange: {
          startDate: rate.from.slice(0, 10),
          endDate: rate.to.slice(0, 10),
        },
      })),
    };
  } else {
    pricing = {
      type: "simple",
      location: yacht.location,
      price: helmPriceInfo?.price ?? 0,
      currency: helmPriceInfo?.currency ?? "EUR",
      per: helmPriceInfo?.per ?? "day",
      vat: helmPriceInfo?.vat ?? 0,
      apa: helmPriceInfo?.apa ?? 0,
    };
  }

  let minDuration: number | "any" = "any";
  let charterStartDay: "1" | "2" | "3" | "4" | "5" | "6" | "0" | "any day" = "any day"; // Mon - Sun

  if (yacht.provider === "BM") {
    minDuration = yacht.meta.bmPriceInfo?.minDuration ?? "any";
    charterStartDay = yacht.meta.bmPriceInfo?.defaultCheckInDay.toString() ?? ("any day" as any);
  }

  if (yacht.provider === "NS") {
    minDuration = yacht.meta.nsCheckIn?.[0].minDuration ?? "any";
    charterStartDay = yacht.meta.nsCheckIn?.[0].checkInDays[0].toString() ?? ("any day" as any);
  }

  if (yacht.provider === "Helm") {
    minDuration = yacht.meta.helmCheckIn?.minDuration ?? "any";
    charterStartDay = yacht.meta.helmCheckIn?.checkInDay ?? ("any day" as any);
  }

  return {
    meta: yachtMetaData(yacht.meta ?? {}),
    basicDetails: {
      photos: yacht.media.map((m, i) => ({
        id: `00000000-0000-0000-0000-000000${i.toString().padStart(6, "0")}`,
        name: "",
        url: m,
      })),
      name: yacht.name,
      model: yacht.model,
      provider: yacht.provider,
      providerId: yacht.externalId,
      manufacturer: yacht.model.manufacturer,
      productionYear: yacht.year,
      startDay: charterStartDay,
      labels: yacht.labels,
      minimumCharterDuration: minDuration,
      type: yacht.boatType,
      operator: yacht.operator,
      charterTypes: {
        skippered: yacht.type === "skippered-only" || yacht.type === "skippered-or-bareboat",
        bareboat: yacht.type === "bareboat" || yacht.type === "skippered-or-bareboat",
        crewed: yacht.type === "crewed",
      },
      offerCreatorVisibility: yacht.visibility.helm_app,
      helmVisibility: yacht.visibility.helm,
      anchorVisibility: yacht.visibility.anchor,
      description: yacht.description,
      notes: yacht.notes,
      ratingAnchor: yacht.ratingAnchor,
      ratingHelm: yacht.ratingHelm,
    },
    locationAndPricing: {
      securityDeposit: {
        price: yacht.securityDepositAmount,
        currency: "EUR",
      },
      pricing,
    },
    specification: {
      length: yacht.length,
      beam: +(findInSpecs("Beam", yacht.specs) ?? 0),
      mainsailType: (findInSpecs("Mainsail Type", yacht.specs) as "racing" | "cruising" | undefined) ?? "not_available",
      cabins: yacht.cabins,
      berths: 0,
      heads: yacht.bathrooms,
      maxPeople: yacht.maxPeople,
      crewCabins: yacht.crewCabins,
      crewNumber: yacht.crewNumber,
      extraSpecs: yacht.specs.map(normalizeSpec).filter((s) => !["Beam", "Mainsail Type"].includes(s.name)),
    },
    amenities: yacht.amenities.map((a) => {
      return {
        id: a.id,
        name: a.text,
        obligatory: a.obligatory,
        formula: a.formula,
        included: a.included,
        provider: a.provider,
        price: {
          price: 0,
          per: "day",
          currency: "EUR",
          where: "locally",
        },
        currency: {
          code: a.currency?.code ?? null,
        },
      };
    }),
  };
}

function yachtMetaData(meta: YachtDetails["meta"]): YachtForm["meta"] {
  return {
    ...emptyYachtMeta,
    checkTimes: {
      checkIn: meta.checkTimes?.checkIn ?? "",
      checkOut: meta.checkTimes?.checkOut ?? "",
    },
    keyFeatures: meta.keyFeatures ?? [],
    brochureLink: meta.brochureLink ?? "",
    videoUrl: meta.videoUrl ?? "",
    planUrl: meta.planUrl ?? "",
    summerPorts: meta.summerPorts ?? "",
    winterPorts: meta.winterPorts ?? "",
    summerAreas: meta.summerAreas ?? "",
    winterAreas: meta.winterAreas ?? "",
    accomodations: meta.accomodations ?? "",
    otherToys: meta.otherToys ?? "",
    brokersNote: {
      brokerId: meta.cmsBrokerNote?.brokerId ?? "",
      text: meta.cmsBrokerNote?.text ?? "",
    },
    sampleMenu: {
      description: meta.sampleMenu?.description ?? "",
      images: meta.sampleMenu?.images ?? [],
    },
    crewInfo: {
      photo: meta.crewInfo?.photo ?? "",
      count: meta.crewInfo?.count ?? null,
      description: meta.crewInfo?.description ?? "",
      languages: meta.crewInfo?.languages ?? "",
      members:
        meta.crewInfo?.members?.map((member) => ({
          img: member.img ?? "",
          name: member.name ?? "",
          title: member.title ?? "",
          nationality: member.nationality ?? "",
          bio: member.bio ?? "",
        })) ?? [],
    },
    perGuestRates:
      meta.perGuestRates?.map((rate) => ({
        seasonName: rate.seasonName ?? "",
        terms: rate.terms ?? "",
        mcaCompliance: rate.mcaCompliance ?? "",
        items: rate.items?.map((item) => ({ guests: item.guests ?? 0, rate: item.rate ?? "" })) ?? [],
      })) ?? [],
  };
}

function normalizeSpec(spec: YachtDetails["specs"][number]) {
  if ("Name" in spec) {
    return {
      name: spec.Name,
      value: spec.Value,
    };
  }
  return spec;
}
function findInSpecs(name: string, specs: YachtDetails["specs"]) {
  const spec = specs.find((s) => {
    if ("Name" in s) {
      return s.Name === name;
    } else {
      return s.name === name;
    }
  });
  if (spec) {
    if ("Value" in spec) {
      return spec.Value;
    } else {
      return spec.value;
    }
  }
}

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

async function loadLocationsByIds(ids: string[]) {
  const locations = await Promise.all(
    ids.map(async (id) => {
      try {
        const response = await fetch<{ data: YachtLocationRef }>(`/api/locations/locations/${id}`);
        return response.data;
      } catch {
        return null;
      }
    }),
  );

  return locations.filter((location): location is YachtLocationRef => location !== null);
}

export function yachtLoader({ params: { id } }: { params: any }) {
  return fetch<{ data: any }>(`/api/yachts/yacht/${id}`).then(async (res) => {
    const checkInInfo = res?.data?.checkInInfo?.[0] || {};
    const generalRateLocationIds: string[] = Array.from(
      new Set((res.data?.meta?.generalRates ?? []).flatMap((rate: { locations: string[] }) => rate.locations)),
    );

    let generalRateLocationsById: Record<string, YachtLocationRef> = {};
    if (generalRateLocationIds.length > 0) {
      const locations = await loadLocationsByIds(generalRateLocationIds);
      generalRateLocationsById = Object.fromEntries(locations.map((location) => [location.id, location]));
    }

    return {
      ...res.data,
      generalRateLocationsById,
      minimumCharterDuration: checkInInfo?.minDuration ?? 1,
      startDay: WEEKDAYS[checkInInfo?.checkInDays?.[0] ?? 0],
    };
  });
}
